import { nsTerminal } from '@/app/i18n/namespaces';
import { FIELD_TYPES } from '@/common-utils/components/renders/detailsGrid/FieldTypes';
import POS_TERMINAL_STATUS from '../enums/PosTerminalStatus';
import PAYLUDD_PERSONALIZATION_INITIALIZATION_STATUS from '@/app/modules/payludd/subscriptions/enums/PayluddPackageStatus';

/**
 * Get the manufacturer item from the selected value in the form
 * @param {String} manufacturerFieldValue Manufacturer selected in the form
 * @param {Array} manufacturersList List of manufacturers
 * @returns {Object} Manufacturer object
 */
const getSelectedManufacturerInForm = (manufacturerFieldValue, manufacturersList) => {
    if (!manufacturerFieldValue || !manufacturersList) {
        return undefined;
    }

    return manufacturersList.find(manufacturerItem => manufacturerItem.value === manufacturerFieldValue);
};

export default {
    UUID: {
        key: 'uuid'
    },
    STATUS: {
        key: 'status',
        entity: 'keys.status',
        type: FIELD_TYPES.BADGE,
        enum: POS_TERMINAL_STATUS,
        namespace: nsTerminal
    },
    SERIAL_NUMBER: {
        key: 'serialNumber',
        type: (props, values = {}) => {
            const manufacturer = getSelectedManufacturerInForm(values.manufacturer, props.manufacturersList);

            if (!manufacturer) {
                return 'text';
            }
            const regexTest = Number(`1e+${manufacturer.length - 1}`);
            return manufacturer.regex.test(regexTest) ? 'tel' : 'text';
        },
        entity: 'modal.info_status.serial_number',
        namespace: nsTerminal
    },
    MANUFACTURER: {
        key: 'manufacturer',
        type: 'select',
        entity: 'modal.info_status.manufacturer',
        namespace: nsTerminal
    },
    LABEL: {
        key: 'label',
        type: 'textarea',
        entity: 'keys.label',
        namespace: nsTerminal
    },
    CUSTOM_LABEL: {
        key: 'customLabel',
        type: 'textarea',
        entity: 'keys.custom_label',
        namespace: nsTerminal
    },
    CREATED_AT: {
        key: 'createdAt',
        type: 'date',
        entity: 'keys.created_at',
        namespace: nsTerminal
    },
    UPDATED_AT: {
        key: 'updatedAt',
        type: 'date',
        entity: 'keys.updated_at',
        namespace: nsTerminal
    },
    BLACKLIST_START_DATE: {
        key: 'blacklistStartDate',
        type: 'date',
        entity: 'keys.blocked_at',
        namespace: nsTerminal
    },
    REWAREHOUSE_DATE: {
        key: 'rewarehousedAt',
        type: 'date',
        entity: 'keys.rewarehouse_at',
        namespace: nsTerminal
    },
    CONNECTION_POINTS: {
        key: 'connectionPoints',
        type: 'array',
        UUID: {
            parentKeys: ['connectionPoints'],
            key: 'uuid',
            type: 'text'
        },
        IP_ADDRESS: {
            parentKeys: ['connectionPoints'],
            key: 'ipAddress',
            type: 'text'
        },
        PORT: {
            parentKeys: ['connectionPoints'],
            key: 'port',
            type: 'text'
        },
        INSTANCE_TAG: {
            parentKeys: ['connectionPoints'],
            key: 'instanceTag',
            type: 'text'
        }
    },
    // info status posTerminal
    STATUS_IN_DATEBASE: {
        key: 'posTerminalDatabaseStatus',
        type: 'badge',
        entity: 'modal.info_status.portal',
        namespace: nsTerminal,
        enum: POS_TERMINAL_STATUS
    },
    REDIS_BLACKLIST_FETCHING_TIME: {
        key: 'redisKeyFetchDuration',
        type: 'fetchingTime',
        entity: 'modal.info_status.fetching_time',
        namespace: 'terminal'
    },
    REDIS_KEYS_FETCHED: {
        key: 'redisKeysFetched',
        entity: 'posTerminal_info_status_key_list_redis',
        REDIS_KEY: {
            parentKeys: ['redisKeysFetched'],
            key: 'key',
            type: 'text',
            namespace: nsTerminal,
            entity: 'modal.info_status.connexion_point'
        },
        REDIS_VALUE: {
            parentKeys: ['redisKeysFetched'],
            key: 'value',
            type: 'object',
            namespace: nsTerminal,
            entity: 'modal.info_status.value'
        },
        REDIS_STATUS: {
            parentKeys: ['redisKeysFetched'],
            key: 'status',
            type: 'badge',
            namespace: nsTerminal,
            entity: 'modal.info_status.status'
        },
        REDIS_START_DATE: {
            parentKeys: ['redisKeysFetched'],
            key: 'startDate',
            type: 'date',
            namespace: nsTerminal,
            entity: 'modal.info_status.starting_date'
        }
    },
    PAYLUDD: {
        key: 'payludd',
        APPLICATION_VERSION: {
            parentKeys: ['payludd', 'version'],
            key: 'application',
            entity: 'payludd.state.application_version',
            namespace: nsTerminal
        },
        PAYLUDD_VERSION: {
            parentKeys: ['payludd', 'version'],
            key: 'payludd',
            entity: 'payludd.state.payludd_version',
            namespace: nsTerminal
        },
        ANDROID_VERSION: {
            parentKeys: ['payludd', 'version'],
            key: 'android',
            entity: 'payludd.state.android_version',
            namespace: nsTerminal
        },
        FIRMWARE: {
            parentKeys: ['payludd'],
            key: 'firmware',
            entity: 'payludd.state.firmware',
            namespace: nsTerminal
        },
        INITIALIZATION_STATUS: {
            parentKeys: ['payludd'],
            key: 'initializationStatus',
            type: FIELD_TYPES.BADGE,
            enum: PAYLUDD_PERSONALIZATION_INITIALIZATION_STATUS,
            entity: 'payludd.state.initialization_status',
            namespace: nsTerminal
        },
        PACKAGE_CODE: {
            parentKeys: ['payludd'],
            key: 'packageCode',
            entity: 'payludd.state.package_code',
            namespace: nsTerminal
        },
        PACKAGE_LABEL: {
            parentKeys: ['payludd'],
            key: 'packageLabel',
            entity: 'payludd.state.package_label',
            namespace: nsTerminal
        },
        COLOR: {
            parentKeys: ['payludd'],
            key: 'color',
            type: FIELD_TYPES.COLOR,
            entity: 'payludd.state.color',
            namespace: nsTerminal
        },
        LOGO: {
            parentKeys: ['payludd'],
            key: 'logo',
            type: FIELD_TYPES.IMAGE,
            entity: 'payludd.state.logo',
            namespace: nsTerminal
        },
        INITIALIZATION_DATE: {
            parentKeys: ['payludd'],
            key: 'initializationDate',
            type: FIELD_TYPES.DATE,
            entity: 'payludd.state.initialization_date',
            namespace: nsTerminal
        },
        POS_TERMINAL_STATE_DATE: {
            parentKeys: ['payludd'],
            key: 'posTerminalStateDate',
            type: FIELD_TYPES.DATE,
            entity: 'payludd.state.last_update',
            namespace: nsTerminal
        }
    },
    PAYLUDD_MPF_DISPLAY: {
        MODE: {
            key: 'payluddMpfMode',
            entity: 'payludd.state.mode',
            namespace: nsTerminal,
            type: FIELD_TYPES.TEXT,
            prefix: 'payludd.mpf.mode.'
        },
        PAYMENT_TICKETS: {
            key: 'payluddMpfPaymentReceiptKeys',
            entity: 'payludd.state.payment_tickets',
            namespace: nsTerminal,
            type: FIELD_TYPES.MULTI_ENTITY,
            entityPrefix: 'payludd.mpf.receipt.'
        },
        ADDITIONAL_SERVICES: {
            key: 'payluddMpfAdditionalServiceKeys',
            entity: 'payludd.state.additional_services',
            namespace: nsTerminal,
            type: FIELD_TYPES.MULTI_ENTITY,
            entityPrefix: 'payludd.mpf.service.'
        },
        BURGER_MENU_LOCK: {
            key: 'payluddMpfBurgerMenuLock',
            entity: 'payludd.state.burger_menu_lock',
            namespace: nsTerminal,
            type: FIELD_TYPES.BOOLEAN
        }
    }
};